import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal, Box, Typography, Button, TextField, IconButton } from '@mui/material';
import { Stack, Autocomplete } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteConfirmModal from './DeleteConfirmModal';
import ClearIcon from '@mui/icons-material/Clear';

function EditClassModal({open, closeModal, selectedClass, updateClass, handleDelete, token}) {

    const [className, setClassName] = useState(selectedClass.name);
    const [startSemester, setStartSemester] = useState(selectedClass.startSemester);
    const [year, setYear] = useState(selectedClass.year);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const [professorOptions, setProfessorOptions] = useState([]);
    const [professors, setProfessors] = useState([]);
    const [professor, setProfessor] = useState({});

    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (professors) {
            let currentProfessors = professors.map((user) => user.user_id);
            setProfessorOptions(professorOptions.filter((user) => !currentProfessors.includes(user.user_id)));
        }
    }, [professors]);

    useEffect(() => {
        async function loadProfessors() {
            const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/class/departments/${selectedClass.department}/coordinators`,
            { headers: {Authorization: `Bearer ${token.access_token}`} })
            let currentProfessors = [];
            if (selectedClass.coordinators) {
                currentProfessors = selectedClass.coordinators.map((user) => user.user_id);
            }
            setProfessor(response.data.filter((user) => !currentProfessors.includes(user.user_id))[0]);
            setProfessorOptions(response.data.filter((user) => !currentProfessors.includes(user.user_id)));
        }
        async function loadActiveProfessors() {
            const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/class/getCoordinators/${selectedClass.class_id}`,
            { headers: {Authorization: `Bearer ${token.access_token}`} })
            let currentProfessors = response.data.map((user) => user.user);
            setProfessors([...currentProfessors]);
        }
        if (open) {
            loadProfessors();
            loadActiveProfessors();
        }
    }, [open])

    const handleClassNameChange = (event, value) => {
        setClassName(value);
    };

    const handleStartSemesterChange = (event, value) => {
        setStartSemester(value);
    };

    const handleYearChange = (event) => {
        setYear(event.target.value);
    };

    const addProfessor = (value) => {
        if (professors.includes(value)) return;
        if (value)
            setProfessors([...professors, value]);
    }

    const removeProfessor = (newProfessor) => {
        setProfessors(professors.filter((professor) => professor.user_id !== newProfessor.user_id));
        setProfessorOptions([...professorOptions, newProfessor]);
    }

    const handleClose = () => {
        setErrorMessage("");
        setProfessors(selectedClass.coordinators);
        closeModal();
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!startSemester || !className) {
            setErrorMessage('No fields can be empty');
            return;
        }

        const data = {
            class_id: selectedClass.class_id,
            name: className,
            sd1_term: startSemester,
            sd1_year: year,
            sd2_term: 'Spring',
            sd2_year: year,
            is_active: false,
            coordinators: professors
        }

        fetch(`${process.env.REACT_APP_API_HOST}/api/class/updateClass`, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${token.access_token}`,
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
            .then(res => res.json())
            .then(json => onJSON(json))
            .catch(reason => console.error('Failed to update class. ', reason) && setErrorMessage('Could not edit class'));

        function onJSON(json) {
            if (json.message) {
                setErrorMessage(json.message);
            }
            else {
                updateClass(data);
                setErrorMessage("");
                closeModal();
            }
        }

    };

    const classes = ['Senior Design 1', 'Senior Design 2'];

    const semesters = ['Fall', 'Spring', 'Summer', 'Summer/Fall'];

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        width: "60%",
        maxWidth: 550,
        minWidth: 300,
        maxHeight: 650,
        p: 3,
        borderRadius: 1,
        boxShadow: 24,
        overflowX: "auto",
    };

    const inputStyle = {
        width: '100%',
        maxWidth: '100ch',
        alignSelf: 'center'
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="edit-class-modal"
            aria-describedby="edit-current-class"
            disableEnforceFocus
        >
            <Box sx={modalStyle}>
                <Stack spacing={2}>
                    <Typography variant='h4'>Edit Class</Typography>
                    <Autocomplete
                        id='class-name'
                        options={classes}
                        value={className}
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        autoHighlight
                        autoSelect
                        freeSolo
                        onChange={handleClassNameChange}
                        sx={inputStyle}
                        renderInput={(params) => (
                            <TextField {...params} label='Class Name' variant='outlined' />
                        )}
                    />
                    <Box style={{ display: 'flex', gap: 30, ...inputStyle }}>
                        <Autocomplete
                            id='start-semester'
                            options={semesters}
                            value={startSemester}
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            autoHighlight
                            autoSelect
                            onChange={handleStartSemesterChange}
                            sx={{ width: '65%', maxWidth: '50ch' }}
                            renderInput={(params) => (
                                <TextField {...params} label='Start Semester' variant='outlined' />
                            )}
                        />
                        <TextField label="Year" type="number" variant="outlined" defaultValue={year} onChange={handleYearChange} style={{width: "35%"}}/>
                    </Box>
                    <Box style={{ display: 'flex', gap: 30, ...inputStyle }}>
                        <Autocomplete
                            id='professors'
                            options={professorOptions}
                            getOptionLabel={(option) => option.first_name + " " + option.last_name}
                            isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            autoHighlight
                            autoSelect
                            onChange={(event, value) => addProfessor(value)}
                            sx={{ width: '65%', maxWidth: '50ch' }}
                            renderInput={(params) => (
                                <TextField {...params} label='Professors' variant='outlined' />
                            )}
                        />
                    </Box>
                    <Stack>
                        {
                            professors && professors.map((professor) => (
                            <Box key={professor.user_id} sx={{display: "flex", justifyContent: "space-between", alignItems: "center", px: 3}}>
                                <Typography>{professor.first_name + " " + professor.last_name}</Typography>
                                <IconButton onClick={() => removeProfessor(professor)}>
                                    <ClearIcon />
                                </IconButton>
                            </Box>
                            ))
                        }
                    </Stack>
                    <Typography style={{ textAlign: 'center', color: 'red' }}>{errorMessage}</Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant='outlined' onClick={e => { e.stopPropagation(); setDeleteOpen(true) }} color='error' startIcon={<DeleteIcon />} fullWidth>Delete</Button>
                        <Button variant='outlined' onClick={handleClose} fullWidth>Cancel</Button>
                        <Button variant='contained' onClick={handleSubmit} fullWidth>Submit</Button>
                    </Stack>
                    <DeleteConfirmModal open={deleteOpen} handleClose={e => { e.stopPropagation(); setDeleteOpen(false);}} type='class' content={className} handleDelete={handleDelete}/>
                </Stack>
            </Box>
        </Modal>
    );
}

export default EditClassModal;